import { Suspense, lazy, useEffect, useState } from 'react';
import { api } from '../../api';
import ChunkErrorBoundary from './ChunkErrorBoundary';
import LegacyPreview from './LegacyPreview';
import ImagePreview from './ImagePreview';
import TextPreview from './TextPreview';

// Heavy renderers are lazy-loaded so a session that only ever opens
// PDFs doesn't pay for SheetJS, and a session that only opens images
// doesn't pay for either. The PDF worker is already in its own chunk
// via Vite's `new URL(..., import.meta.url)` resolution.
const PdfPreview = lazy(() => import('./PdfPreview'));
const SpreadsheetPreview = lazy(() => import('./SpreadsheetPreview'));
const DocxPreview = lazy(() => import('./DocxPreview'));

const LoadingFallback = () => (
  <div className="dd-loading" style={{ padding: 16 }}>Loading viewer…</div>
);

// Top-level preview dispatcher. Calls /preview-manifest once per
// document, then mounts the renderer that matches manifest.kind.
//
// Adding a new format = add a branch + (optionally) a new manifest kind
// on the backend. Callers don't change.
export default function DocumentPreview({
  doc,
  documentId,
  initialPage = null,
  query = null,
  chunkId = null,
  citeRange = null,
  anchor = null,
  onHighlightStatus = null,
  segments = null,
  onSegmentRuling = null,
  segmentBusy = false,
  jumpRequest = null,
  onActivePageChange = null,
  onResolvedSnippet = null,
}) {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setManifest(null);
    setError(null);

    api.get(`/documents/${documentId}/preview-manifest`)
      .then(m => { if (!cancelled) setManifest(m); })
      .catch(err => { if (!cancelled) setError(err.message); });

    return () => { cancelled = true; };
  }, [documentId]);

  // On manifest fetch failure, drop straight into the legacy preview so
  // users always see something rather than an error placeholder.
  if (error) {
    return (
      <LegacyPreview
        doc={doc}
        documentId={documentId}
        totalPages={doc?.extraction?.page_count || 0}
        initialPage={initialPage}
      />
    );
  }
  if (!manifest) {
    return <div className="dd-loading" style={{ padding: 16 }}>Loading preview…</div>;
  }

  switch (manifest.kind) {
    case 'pdf':
      return (
        <ChunkErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <PdfPreview
              documentId={documentId}
              manifest={manifest}
              initialPage={initialPage}
              query={query}
              chunkId={chunkId}
              citeRange={citeRange}
              anchor={anchor}
              onHighlightStatus={onHighlightStatus}
              onResolvedSnippet={onResolvedSnippet}
              segments={segments}
              onSegmentRuling={onSegmentRuling}
              segmentBusy={segmentBusy}
              jumpRequest={jumpRequest}
              onActivePageChange={onActivePageChange}
            />
          </Suspense>
        </ChunkErrorBoundary>
      );

    case 'spreadsheet':
      return (
        <ChunkErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <SpreadsheetPreview documentId={documentId} manifest={manifest} />
          </Suspense>
        </ChunkErrorBoundary>
      );

    case 'docx':
      return (
        <ChunkErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <DocxPreview documentId={documentId} manifest={manifest} query={query} onHighlightStatus={onHighlightStatus} />
          </Suspense>
        </ChunkErrorBoundary>
      );

    case 'image':
      return <ImagePreview documentId={documentId} manifest={manifest} doc={doc} />;

    case 'text':
      return <TextPreview documentId={documentId} manifest={manifest} query={query} onHighlightStatus={onHighlightStatus} />;

    // Audio / video continue to use the existing /media-url + transcript
    // flow that already lives outside this preview pane. The legacy
    // filmstrip handles keyframes for media docs.
    case 'media':
    // Phase 6 (LibreOffice catch-all) populates page_count for these.
    // Until that lands, the legacy renderer at least shows what's there
    // (extraction preview_images for some, "no preview" for others).
    case 'paged-fallback':
    case 'none':
    default:
      return (
        <LegacyPreview
          doc={doc}
          documentId={documentId}
          totalPages={manifest.page_count || doc?.extraction?.page_count || 0}
          initialPage={initialPage}
        />
      );
  }
}
